import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./hooks/useAuth";
import Layout from "./components/Layout";

// Static imports for public pages (required for react-snap pre-rendering)
import Home from "./pages/Home";
import Services from "./pages/Services";
import PaintProtectionFilm from "./pages/services/PaintProtectionFilm";
import CeramicCoatings from "./pages/services/CeramicCoatings";
import WindowTint from "./pages/services/WindowTint";
import VinylWraps from "./pages/services/VinylWraps";
import WindshieldProtection from "./pages/services/WindshieldProtection";
import PPFSummerlin from "./pages/services/PPFSummerlin";
import PPFQuoteLanding from "./pages/PPFQuoteLanding";
import CeramicCoatingHenderson from "./pages/services/CeramicCoatingHenderson";
import TeslaProtection from "./pages/TeslaProtection";
import Gallery from "./pages/Gallery";
import Resources from "./pages/Resources";
import BlogPost from "./pages/resources/BlogPost";
import PpfVsCeramicCoating from "./pages/resources/PpfVsCeramicCoating";
import PpfLeasedCar from "./pages/resources/PpfLeasedCar";
import PpfInstallerMistakes from "./pages/resources/PpfInstallerMistakes";
import About from "./pages/About";
import Contact from "./pages/Contact";
import ThankYou from "./pages/ThankYou";
import NotFound from "./pages/NotFound";

// Lazy imports only for admin routes (blocked by robots.txt)
const Admin = lazy(() => import("./pages/admin/Admin"));
const GalleryManagement = lazy(() => import("./pages/admin/GalleryManagement"));
const Submissions = lazy(() => import("./pages/admin/Submissions"));
const TestimonialManagement = lazy(() => import("./pages/admin/TestimonialManagement"));
const VideoManagement = lazy(() => import("./pages/admin/VideoManagement"));

const queryClient = new QueryClient();

const LoadingFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-accent animate-pulse text-lg font-heading">Loading...</div>
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/" element={<Layout />}>
                <Route index element={<Home />} />
                <Route path="services" element={<Services />} />
                <Route path="services/paint-protection-film" element={<PaintProtectionFilm />} />
                <Route path="services/ceramic-coatings" element={<CeramicCoatings />} />
                <Route path="services/window-tint" element={<WindowTint />} />
                <Route path="services/vinyl-wraps" element={<VinylWraps />} />
                <Route path="services/windshield-protection" element={<WindshieldProtection />} />
                <Route path="services/window-tint-wraps" element={<Navigate to="/services/window-tint" replace />} />
                <Route path="ppf-in-summerlin" element={<PPFSummerlin />} />
                <Route path="ceramic-coating-henderson" element={<CeramicCoatingHenderson />} />
                <Route path="tesla-protection" element={<TeslaProtection />} />
                <Route path="ppf-quote" element={<PPFQuoteLanding />} />
                <Route path="gallery" element={<Gallery />} />
                <Route path="resources" element={<Resources />} />
                <Route path="resources/ppf-vs-ceramic-coating-nevada-sun" element={<PpfVsCeramicCoating />} />
                <Route path="resources/ppf-worth-it-leased-car-las-vegas" element={<PpfLeasedCar />} />
                <Route path="resources/5-mistakes-choosing-ppf-installer-las-vegas" element={<PpfInstallerMistakes />} />
                <Route path="resources/:slug" element={<BlogPost />} />
                <Route path="about" element={<About />} />
                <Route path="contact" element={<Contact />} />
                <Route path="thank-you" element={<ThankYou />} />
                <Route path="*" element={<NotFound />} />
              </Route>
              <Route path="/admin" element={<Suspense fallback={<LoadingFallback />}><Admin /></Suspense>} />
              <Route path="/admin/gallery" element={<Suspense fallback={<LoadingFallback />}><GalleryManagement /></Suspense>} />
              <Route path="/admin/submissions" element={<Suspense fallback={<LoadingFallback />}><Submissions /></Suspense>} />
              <Route path="/admin/testimonials" element={<Suspense fallback={<LoadingFallback />}><TestimonialManagement /></Suspense>} />
              <Route path="/admin/videos" element={<Suspense fallback={<LoadingFallback />}><VideoManagement /></Suspense>} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
